/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { isEqual } from 'lodash';
import { useEffect, useRef } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getLinkedTargetIds, selectSharedData } from './selectors';
import { syncGroupsActions } from './syncGroups';
import type { CoreAppStore } from '../types';
import type { PotentialData } from './syncGroups/types';

export const useUpdateSharedData = (
  data: PotentialData,
  panelId?: string,
  options?: { requireLinkedMaps?: boolean },
): PotentialData => {
  const resolvedPanelId = panelId ?? '';
  const linkedMaps = useSelector(
    (state: CoreAppStore) => getLinkedTargetIds(state, panelId!),
    shallowEqual,
  );
  const dispatch = useDispatch();
  const requireLinkedMaps = options?.requireLinkedMaps ?? true;

  const stableDataRef = useRef<PotentialData>(data);
  if (!isEqual(stableDataRef.current, data)) {
    stableDataRef.current = data;
  }
  const stableData = stableDataRef.current;

  const lastDispatchedRef = useRef<{
    panelId: string | undefined;
    linkedMaps: string[];
    data: PotentialData;
  } | null>(null);

  useEffect(() => {
    const canUpdateSharedData = requireLinkedMaps
      ? linkedMaps?.length > 0
      : true;
    if (!panelId || !canUpdateSharedData) {
      return;
    }
    const last = lastDispatchedRef.current;
    if (
      last?.panelId === panelId &&
      shallowEqual(last.linkedMaps, linkedMaps) &&
      isEqual(last.data, stableData)
    ) {
      return;
    }
    lastDispatchedRef.current = { panelId, linkedMaps, data: stableData };

    dispatch(
      syncGroupsActions.addFeatureSyncState({
        panelId,
        data: stableData,
      }),
    );
  }, [panelId, linkedMaps, stableData, dispatch, requireLinkedMaps]);

  useEffect(() => {
    return (): void => {
      if (panelId) {
        const cleanupData = lastDispatchedRef.current?.data;
        lastDispatchedRef.current = null;
        const resetData: PotentialData = {};

        Object.keys(cleanupData?.featureLayersById ?? {}).forEach((layerId) => {
          dispatch(syncGroupsActions.deleteFeatureLayer({ panelId, layerId }));
        });

        if (cleanupData?.formFeatures !== undefined) {
          resetData.formFeatures = [];
        }

        if (cleanupData?.selectedFeatureId !== undefined) {
          resetData.selectedFeatureId = undefined;
        }

        if (cleanupData?.hoverId !== undefined) {
          resetData.hoverId = undefined;
        }

        if (Object.keys(resetData).length > 0) {
          dispatch(
            syncGroupsActions.addFeatureSyncState({
              panelId,
              data: resetData,
            }),
          );
        }
      }
    };
  }, [dispatch, panelId]);
  return useSelector((state: CoreAppStore) =>
    selectSharedData(state, resolvedPanelId),
  );
};
